import React, { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { downloadReceiptPDF, printReceipt } from "@/lib/receipt";
import { CheckCircle2, Loader2, CreditCard, Smartphone, Download, Printer, Clock, X } from "lucide-react";

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

export default function PayPage() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = sp.get("session_id");
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");
  const [paying, setPaying] = useState(false);
  const [checking, setChecking] = useState(!!sessionId);
  const [tab, setTab] = useState("card");

  const load = useCallback(async () => {
    try { const r = await api.get(`/orders/pay/${token}`); setOrder(r.data); }
    catch (e) { setErr(e?.response?.data?.detail || "Order not found"); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Poll payment status after returning from Stripe
  useEffect(() => {
    if (!sessionId) return;
    let tries = 0;
    const iv = setInterval(async () => {
      tries += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") { clearInterval(iv); setChecking(false); load(); }
      } catch { /* keep polling */ }
      if (tries > 20) { clearInterval(iv); setChecking(false); }
    }, 2000);
    return () => clearInterval(iv);
  }, [sessionId, load]);

  const payCard = async () => {
    setPaying(true);
    try {
      const r = await api.post(`/orders/pay/${token}/checkout`, { origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) { setErr(e?.response?.data?.detail || "Could not start payment"); setPaying(false); }
  };

  const pdfOrder = order && {
    invoice_number: order.invoice_number || order.order_number, items: order.items,
    subtotal: order.subtotal, item_discount_total: order.item_discount_total, discount_total: order.discount_total,
    hst_rate: order.hst_rate, hst_amount: order.hst_amount, total: order.total,
    payment_method: order.paid_method, created_at: order.created_at,
  };
  const recSettings = order && {
    ...order.store, store_name: order.store?.name, store_address_block: order.store?.address_block,
    store_website: order.store?.website, store_tax_number: order.store?.tax_number,
    receipt_logo: order.store?.logo, return_policy: order.return_policy,
  };

  if (err) return <Center><div className="text-brand-red font-bold" data-testid="pay-error">{err}</div></Center>;
  if (!order) return <Center><Loader2 className="h-6 w-6 animate-spin text-brand-navy" /></Center>;

  const paid = ["READY", "PAID", "PICKED_UP"].includes(order.status);
  const dead = ["EXPIRED", "CANCELLED"].includes(order.status);
  const isHold = order.order_kind === "HOLD";
  const awaitingDeposit = isHold && order.status === "AWAITING_DEPOSIT";
  const held = isHold && order.status === "HELD";
  const payAmount = held ? (order.balance_due ?? order.total) : order.total;

  const payDeposit = async () => {
    setPaying(true);
    try {
      const r = await api.post(`/orders/pay/${token}/deposit`, { origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) { setErr(e?.response?.data?.detail || "Could not start deposit"); setPaying(false); }
  };

  return (
    <div className="min-h-screen bg-slate-100 py-6 px-4" data-testid="pay-page">
      <div className="mx-auto max-w-md bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="bg-brand-navy text-white p-5 text-center relative">
          <button
            onClick={() => { if (window.history.length > 1) navigate(-1); else navigate("/"); }}
            data-testid="pay-close-btn"
            aria-label="Close"
            title="Close — your order stays held for 24 hours"
            className="absolute top-3 right-3 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-1.5 transition"
          >
            <X className="h-5 w-5" />
          </button>
          {order.store?.logo
            ? <img src={order.store.logo} alt="logo" className="h-12 mx-auto object-contain" />
            : <div className="font-heading text-xl">{order.store?.name || "DealsWagon Outlet"}</div>}
          {(order.store?.address_block || "").split("\n").filter(Boolean).map((l, i) => (
            <div key={i} className="text-xs text-white/70">{l}</div>
          ))}
          <div className="mt-2 text-sm">Order <b data-testid="pay-order-number">{order.order_number}</b></div>
        </div>

        <div className="p-5">
          {order.paylink_message && !paid && !dead && (
            <p className="text-sm text-slate-600 mb-3 whitespace-pre-line" data-testid="pay-intro">{order.paylink_message}</p>
          )}
          <div className="divide-y">
            {order.items.map((l) => (
              <div key={l.line_id} className="flex justify-between py-2 text-sm" data-testid="pay-line">
                <span>{l.quantity}× {l.product_name} <span className="text-slate-400">({l.sku})</span></span>
                <span className="font-bold">{money(l.line_total)}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 space-y-1 text-sm">
            <Row k="Subtotal" v={money(order.subtotal)} />
            {order.discount_total > 0 && <Row k="Cart discount" v={`−${money(order.discount_total)}`} />}
            <Row k={`HST (${order.hst_rate}%)`} v={money(order.hst_amount)} />
            <div className="flex justify-between font-heading text-2xl text-brand-navy pt-1">
              <span>Total</span><span data-testid="pay-total">{money(order.total)} {order.currency}</span>
            </div>
            {held && (
              <>
                <Row k="Deposit paid" v={`−${money(order.deposit_amount)}`} />
                <div className="flex justify-between font-bold text-brand-red pt-1"><span>Balance due</span><span data-testid="pay-balance">{money(order.balance_due)} {order.currency}</span></div>
              </>
            )}
          </div>

          {paid && (
            <div className="mt-5 text-center" data-testid="pay-confirmed">
              <CheckCircle2 className="h-12 w-12 text-brand-green mx-auto" />
              <div className="font-heading text-xl text-brand-navy mt-1">Paid — ready for pickup</div>
              <div className="text-sm text-slate-500">Invoice {order.invoice_number} · {order.paid_method}</div>
              <div className="mt-4 flex items-center justify-center gap-2">
                <button onClick={() => printReceipt(pdfOrder, recSettings)} data-testid="pay-print-invoice" className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-brand-navy text-white font-bold"><Printer className="h-4 w-4" /> Print</button>
                <button onClick={() => downloadReceiptPDF(pdfOrder, recSettings)} data-testid="pay-download-invoice" className="inline-flex items-center gap-2 h-11 px-5 rounded-lg border border-brand-navy text-brand-navy font-bold"><Download className="h-4 w-4" /> Download PDF</button>
              </div>
            </div>
          )}

          {dead && (
            <div className="mt-5 text-center text-brand-red font-bold" data-testid="pay-dead">
              {order.deposit_forfeited
                ? `This 24-hour hold expired. The ${money(order.deposit_amount)} deposit was kept as a no-show fee.`
                : `This order is ${order.status.toLowerCase()}. Please ask staff for a new link.`}
            </div>
          )}

          {awaitingDeposit && (
            <div className="mt-5" data-testid="pay-deposit-step">
              <div className="bg-brand-yellowLight border border-brand-yellow rounded-xl p-4 text-sm text-slate-700 mb-3">
                Pay a <b>{money(order.deposit_amount)}</b> deposit to hold this item for 24 hours. It's credited toward your total.
              </div>
              <button onClick={payDeposit} disabled={paying} data-testid="pay-deposit-btn" className="w-full h-14 rounded-xl bg-brand-green text-white font-heading text-lg disabled:opacity-60">
                {paying ? "Redirecting…" : `Pay ${money(order.deposit_amount)} deposit`}
              </button>
            </div>
          )}

          {!paid && !dead && !awaitingDeposit && (
            <div className="mt-5">
              {held && <div className="text-sm text-slate-600 mb-3" data-testid="pay-hold-note">Your item is held. Pay the <b>{money(payAmount)}</b> balance below to complete your order before pickup.</div>}
              {checking && <div className="flex items-center justify-center gap-2 text-slate-500 mb-3"><Loader2 className="h-4 w-4 animate-spin" /> Confirming payment…</div>}
              <div className="grid grid-cols-2 gap-2 mb-3">
                <button onClick={() => setTab("card")} data-testid="pay-tab-card" className={`h-11 rounded-lg font-bold inline-flex items-center justify-center gap-2 ${tab === "card" ? "bg-brand-navy text-white" : "bg-slate-100 text-slate-600"}`}><CreditCard className="h-4 w-4" /> Card</button>
                <button onClick={() => setTab("etransfer")} data-testid="pay-tab-etransfer" className={`h-11 rounded-lg font-bold inline-flex items-center justify-center gap-2 ${tab === "etransfer" ? "bg-brand-navy text-white" : "bg-slate-100 text-slate-600"}`}><Smartphone className="h-4 w-4" /> e-Transfer</button>
              </div>
              {tab === "card"
                ? <button onClick={payCard} disabled={paying} data-testid="pay-now-card" className="w-full h-14 rounded-xl bg-brand-green text-white font-heading text-lg disabled:opacity-60">{paying ? "Redirecting…" : `Pay ${money(payAmount)} now`}</button>
                : <div className="bg-slate-50 rounded-xl p-4 text-sm text-slate-700" data-testid="pay-etransfer-instructions">
                    <div className="font-bold text-brand-navy mb-1">Pay by Interac e-Transfer</div>
                    <p className="whitespace-pre-line">{order.etransfer}</p>
                    <p className="mt-2">Amount: <b>{money(payAmount)} {order.currency}</b> · Reference: <b>{order.order_number}</b></p>
                    <p className="mt-2 text-xs text-slate-500 flex items-center gap-1"><Clock className="h-3 w-3" /> Staff will mark your order paid once the transfer is received.</p>
                  </div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Center = ({ children }) => <div className="min-h-screen grid place-items-center bg-slate-100 p-4">{children}</div>;
const Row = ({ k, v }) => <div className="flex justify-between text-slate-600"><span>{k}</span><span>{v}</span></div>;
